import React from 'react';
import { Bell, User, ChevronDown, LogOut, Settings, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { DS } from '../../lib/design-system';

interface HeaderProps {
  isMobile: boolean;
  isTablet?: boolean;
  onMenuClick?: () => void;
  isProfileMenuOpen: boolean;
  setIsProfileMenuOpen: (open: boolean) => void;
  profileMenuRef: React.RefObject<HTMLDivElement | null>;
  logoUrl: string;
  onSettingsClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({
  isMobile,
  isTablet = false,
  onMenuClick,
  isProfileMenuOpen,
  setIsProfileMenuOpen,
  profileMenuRef,
  logoUrl,
  onSettingsClick,
}) => {
  const { user, signOut } = useAuth();
  const { currentOrg, organizations, switchOrganization } = useOrganization();

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  return (
    <header className={`sticky top-0 flex-none h-14 md:h-16 bg-white/90 backdrop-blur-md border-b border-gray-200 flex items-center justify-between px-4 md:px-6 lg:px-8 z-40 shadow-sm ${DS.transitions.all} ${DS.transitions.normal}`}>
      {/* Mobile: Logo only (navigation via bottom nav) */}
      {isMobile && (
        <div className="flex items-center">
          <img src={logoUrl} alt="Logo" className="w-8 h-8" />
        </div>
      )}

      {/* Tablet: Logo + App name (sidebar is collapsed by default) */}
      {isTablet && (
        <div className="flex items-center gap-2.5">
          <img src={logoUrl} alt="Logo" className="w-8 h-8" />
          <span className="font-bold text-[#5D1826] text-sm tracking-tight">Gestor Municipal</span>
        </div>
      )}

      {/* Desktop: Empty spacer (sidebar shows branding) */}
      {!isMobile && !isTablet && <div></div>}

      <div className="flex items-center gap-2 md:gap-4">
        {/* Notifications */}
        <motion.button
          whileTap={DS.animations.buttonTap}
          className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        >
          <Bell size={20} />
          <span className="absolute top-2 right-2 w-2 h-2 bg-amber-500 rounded-full border-2 border-white"></span>
        </motion.button>

        {/* User Profile Menu */}
        <div className="relative" ref={profileMenuRef}>
          <motion.button
            whileTap={DS.animations.buttonTap}
            onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-[#5D1826] to-[#7A2435] border-2 border-white shadow-md flex items-center justify-center">
              <User size={16} className="text-white" />
            </div>
            {!isMobile && (
              <ChevronDown
                size={16}
                className={`text-gray-500 transition-transform duration-200 ${isProfileMenuOpen ? 'rotate-180' : ''}`}
              />
            )}
          </motion.button>

          <AnimatePresence>
            {isProfileMenuOpen && (
              <motion.div
                {...DS.animations.dropdown}
                className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50"
              >
                {/* User Info Section */}
                <div className="p-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-[#5D1826] to-[#7A2435] flex items-center justify-center">
                      <User size={20} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user?.email}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {currentOrg?.name || 'Sin organización'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Organization Switcher */}
                {organizations && organizations.length > 1 && (
                  <div className="p-2 border-b border-gray-200">
                    <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Cambiar Organización
                    </p>
                    {organizations.map((org) => (
                      <motion.button
                        key={org.id}
                        whileTap={DS.animations.subtleTap}
                        onClick={() => {
                          switchOrganization(org.id);
                          setIsProfileMenuOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                          currentOrg?.id === org.id
                            ? 'bg-[#5D1826]/10 text-[#5D1826] font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Building2 size={16} />
                        <span className="flex-1 text-left truncate">{org.name}</span>
                        {currentOrg?.id === org.id && (
                          <div className="w-2 h-2 rounded-full bg-[#5D1826]" />
                        )}
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Menu Options */}
                <div className="p-2">
                  <motion.button
                    whileTap={DS.animations.subtleTap}
                    onClick={() => {
                      if (onSettingsClick) {
                        onSettingsClick();
                        setIsProfileMenuOpen(false);
                      }
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <Settings size={16} />
                    <span>Configuración</span>
                  </motion.button>
                  <motion.button
                    whileTap={DS.animations.subtleTap}
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut size={16} />
                    <span>Cerrar Sesión</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};
